import React, { useEffect, useState } from 'react';
import { Field, Form, withFormik, getIn } from 'formik';
import * as Yup from 'yup';
import { useDispatch, connect } from 'react-redux';
import { Button, Col, Row } from 'react-bootstrap';
import camelCaseRecursive from 'camelcase-keys-recursive';
import { FormikInput, FormikSelect, Icon, InputSelect, LinkBtn, NestedAttributes } from '../../components';
import { debounceIndexBalancesRequest } from '../../requests/balances';
import BookkeeperItems from './BookkeeperItems';
import { indexAdvanceSettingsRequest } from '../../requests/advanceSettings';
import { debounceIndexJobManagementsRequest } from '../../requests/jobManagements';
import { debounceIndexBranchOfficesRequest } from '../../requests/branchOffices';
import { debounceIndexCostCentersRequest } from '../../requests/costCenters';

const options = [
  { value: 'yes', label: 'Sí' },
  { value: 'no', label: 'No' }
];

const imputationOption = [
  { value: 'debit', label: 'Débito' },
  { value: 'credit', label: 'Crédito' }
];

const resultFetchData = (response, withCode) => {
  const { data } = response.data;
  const tempArray = data.map(element => {
    return {
      label: withCode ? `${element.code} - ${element.name}` : element.name,
      value: element.id
    };
  });
  return tempArray;
};

const BookkeeperNewForm = props => {
  const { values, submitVariant, errors, touched, setFieldValue, setFieldTouched, isSubmitting } = props;
  const dispatch = useDispatch();
  const { bookkeeper } = values;
  const { bookkeeperItems } = bookkeeper;
  const [selectOptions, setSelectOptions] = useState([]);

  const fetchJobManagments = () => {
    let isMounted = true;

    debounceIndexJobManagementsRequest({
      dispatch,
      params: {
        actives: true,
        sort_column: 'name',
        display_length: 500
      },
      successCallback: data => {
        if (isMounted) {
          setSelectOptions(resultFetchData(data));
        }
      }
    });

    return () => {
      isMounted = false;
    };
  };

  const fetchBranchOffices = () => {
    const isMounted = true;
    debounceIndexBranchOfficesRequest({
      dispatch,
      params: {
        actives: true,
        sort_column: 'name',
        display_length: 500
      },
      successCallback: data => {
        if (isMounted) {
          setSelectOptions(resultFetchData(data));
        }
      }
    });
  };

  const fetchCostCenters = () => {
    const isMounted = true;
    debounceIndexCostCentersRequest({
      dispatch,
      params: {
        actives: true,
        sort_column: 'name',
        display_length: 500
      },
      successCallback: data => {
        if (isMounted) {
          setSelectOptions(resultFetchData(data));
        }
      }
    });
  };

  const handleActions = condition => {
    switch (condition) {
      case 'job_management':
        fetchJobManagments();
        break;
      case 'branch_office':
        fetchBranchOffices();
        break;
      case 'cost_center':
        fetchCostCenters();
        break;
      default:
        // eslint-disable-next-line no-console
        console.log('Error: Action not found');
        break;
    }
  };

  const setConditionResource = settingOptions => {
    const settIndex = settingOptions.findIndex(value => value.code === 'condition_for_bookkeeper_association');
    const conditionResource = settIndex > 0 ? settingOptions[settIndex]?.textValue : '';
    handleActions(conditionResource);
  };

  const settingIndex = () => {
    indexAdvanceSettingsRequest({
      dispatch,
      successCallback: response => {
        setConditionResource(camelCaseRecursive(response.data));
      }
    });
  };

  const fetchBalances = (inputValue, callback) => {
    debounceIndexBalancesRequest({
      dispatch,
      params: {
        query: inputValue,
        sort_column: 'code',
        paginate: false,
        inactive_exceptions: true
      },
      successCallback: response => {
        callback(resultFetchData(response, true));
      }
    });
  };

  const addBookkeepers = () => {
    const mapResults = bookkeeperItems.map((body, index) => {
      if (body._destroy) {
        return undefined;
      }

      return (
        <BookkeeperItems
          key={`account-${index.toString()}`}
          errors={errors}
          touched={touched}
          index={index}
          bookkeeperAttribute={body}
          setFieldValue={setFieldValue}
          setFieldTouched={setFieldTouched}
          values={values}
          getIn={getIn}
          selectOptions={selectOptions}
        />
      );
    });

    mapResults.push(
      <>
        <Col md={4} className="sample-row">
          <FormikInput label="Cuenta Contable" disabled />
        </Col>
        <Col md={4} xl={3} className="sample-row">
          <InputSelect label="Condición" placeholder="Seleccionar Condición" disabled />
        </Col>
        <Col md={3} xl={4} className="sample-row">
          <FormikInput label="Cuenta Mayor" disabled />
        </Col>
      </>
    );

    return (
      <>
        <NestedAttributes
          removeFirstItem
          mapInputs={mapResults}
          arrayValues={bookkeeperItems}
          setFieldValue={setFieldValue}
          valuePath="bookkeeper[bookkeeperItems]"
          newAttributes={{ account: '', recordId: '', majorAccount: '' }}
        />
      </>
    );
  };

  useEffect(settingIndex, []);

  return (
    <>
      <Row>
        <Col md={12}>
          <Form>
            <Row>
              <Col md={9}>
                <Field name="bookkeeper[balanceId]">
                  {({ field }) => (
                    <InputSelect
                      {...field}
                      abbr
                      isClearable
                      label="Concepto"
                      placeholder="Seleccionar Concepto"
                      value={values.balanceId}
                      request={fetchBalances}
                      onChange={data => setFieldValue(field.name, data ? data.value : '')}
                      error={getIn(errors, field.name)}
                      touched={getIn(touched, field.name)}
                    />
                  )}
                </Field>
              </Col>
            </Row>
            <Row>
              <Col md={3}>
                <Field name="bookkeeper[distribution]">
                  {({ field }) => (
                    <FormikSelect
                      {...field}
                      abbr
                      label="Distribución"
                      placeholder="Seleccionar"
                      options={options}
                      defaultValue="yes"
                      onChange={data => setFieldValue(field.name, data ? data.value : '')}
                      setFieldTouched={() => setFieldTouched(field.name)}
                      error={getIn(errors, field.name)}
                      touched={getIn(touched, field.name)}
                    />
                  )}
                </Field>
              </Col>

              <Col md={3}>
                <Field name="bookkeeper[grouped]">
                  {({ field }) => (
                    <FormikSelect
                      {...field}
                      abbr
                      label="Agrupado"
                      placeholder="Seleccionar"
                      options={options}
                      defaultValue="yes"
                      onChange={data => setFieldValue(field.name, data ? data.value : '')}
                      setFieldTouched={() => setFieldTouched(field.name)}
                      error={getIn(errors, field.name)}
                      touched={getIn(touched, field.name)}
                    />
                  )}
                </Field>
              </Col>
              <Col md={3}>
                <Field name="bookkeeper[imputation]">
                  {({ field }) => (
                    <FormikSelect
                      {...field}
                      abbr
                      label="Imputación"
                      placeholder="Seleccionar"
                      options={imputationOption}
                      onChange={data => setFieldValue(field.name, data ? data.value : '')}
                      setFieldTouched={() => setFieldTouched(field.name)}
                      error={getIn(errors, field.name)}
                      touched={getIn(touched, field.name)}
                    />
                  )}
                </Field>
              </Col>
            </Row>
            <hr />
            <h4 className="text-uppercase mb-3">Asociación de Cuentas Contables</h4>
            {getIn(touched, 'bookkeeper.bookkeeperItems') &&
              getIn(errors, 'bookkeeper.bookkeeperItems') === 'Debes asociar al menos una cuenta contable' && (
                <Row>
                  <Col md={12}>
                    <span style={{ color: 'red' }}>
                      <Icon icon="alert-circle" style={{ marginRight: '5px', height: '15px', width: '15px' }} />
                      {getIn(errors, 'bookkeeper.bookkeeperItems')}
                    </span>
                  </Col>
                </Row>
              )}
            <div className="bg-color-course p-course b-radius">{addBookkeepers()}</div>
            <hr />
            <Row className="d-flex justify-content-end mt-1 mb-3">
              <Col md={2}>
                <LinkBtn variant="dark" className="btn-secondary btn-block" block to="/remunerations_settings">
                  Cancelar
                </LinkBtn>
              </Col>
              <Col md={2}>
                <Button type="submit" className="btn-block" variant={submitVariant} disabled={isSubmitting}>
                  Guardar
                </Button>
              </Col>
            </Row>
          </Form>
        </Col>
      </Row>
    </>
  );
};

const setInitialValues = () => {
  return {
    bookkeeper: {
      balanceId: '',
      recordId: '',
      distribution: '',
      grouped: '',
      imputation: '',
      account: '',
      major_account: '',
      bookkeeperItems: []
    }
  };
};

const validationSchema = Yup.object().shape({
  bookkeeper: Yup.object().shape({
    balanceId: Yup.string().required('Debes seleccionar un concepto'),
    distribution: Yup.string().required('Debes seleccionar una distribución'),
    grouped: Yup.string().required('Debes seleccionar un agrupado'),
    imputation: Yup.string().required('Debes seleccionar una imputación'),
    bookkeeperItems: Yup.array()
      .of(
        Yup.object().shape({
          account: Yup.string().required('Debes seleccionar una cuenta contable')
        })
      )
      .min(1, 'Debes asociar al menos una cuenta contable')
  })
});

const handleSubmit = (values, { props, setSubmitting }) => {
  const { formRequest } = props;
  formRequest(values, setSubmitting);
};

export default withFormik({
  mapPropsToValues: props => setInitialValues(props),
  validationSchema,
  handleSubmit,
  enableReinitialize: true,
  validateOnMount: true
})(connect()(BookkeeperNewForm));
